/**
 * Zustand store slice — Write tab state.
 *
 * Manages the library / prompt selection flow, variable values, and the
 * in-progress generation job state for the Write tab.
 */

import { create } from 'zustand';
import type { PromptLibraryOut, PromptOut, GenerateContent } from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WriteState {
  /** Libraries fetched from the service. */
  libraries: PromptLibraryOut[];
  /** Currently selected library id. */
  selectedLibraryId: string | null;
  /** Prompts for the selected library. */
  prompts: PromptOut[];
  /** Currently selected prompt id. */
  selectedPromptId: string | null;
  /** Variable values keyed by variable name (user-entered values). */
  variables: Record<string, string>;

  /** Loading flags */
  isLoadingLibraries: boolean;
  isLoadingPrompts: boolean;
  isGenerating: boolean;

  /** Result of the last completed generation job. */
  result: GenerateContent | null;

  /** Interim Aries status messages while a job is processing. */
  generationProgress: string[];

  /** Tab-level error message. */
  writeError: string | null;

  // -- Actions ---------------------------------------------------------------

  setLibraries: (libs: PromptLibraryOut[]) => void;
  setSelectedLibraryId: (id: string | null) => void;
  setPrompts: (prompts: PromptOut[]) => void;
  setSelectedPromptId: (id: string | null) => void;
  setVariable: (name: string, value: string) => void;
  setVariables: (vars: Record<string, string>) => void;
  setIsLoadingLibraries: (v: boolean) => void;
  setIsLoadingPrompts: (v: boolean) => void;
  setIsGenerating: (v: boolean) => void;
  setResult: (r: GenerateContent | null) => void;
  setGenerationProgress: (messages: string[]) => void;
  setWriteError: (e: string | null) => void;
  /** Clear result + error, keep selection and variable values. */
  clearResult: () => void;
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useWriteStore = create<WriteState>((set) => ({
  libraries: [],
  selectedLibraryId: null,
  prompts: [],
  selectedPromptId: null,
  variables: {},
  isLoadingLibraries: false,
  isLoadingPrompts: false,
  isGenerating: false,
  result: null,
  generationProgress: [],
  writeError: null,

  setLibraries: (libraries) => set({ libraries }),
  setSelectedLibraryId: (selectedLibraryId) =>
    set({ selectedLibraryId, selectedPromptId: null, variables: {}, result: null }),
  setPrompts: (prompts) => set({ prompts }),
  setSelectedPromptId: (selectedPromptId) =>
    set({ selectedPromptId, variables: {}, result: null }),
  setVariable: (name, value) =>
    set((state) => ({ variables: { ...state.variables, [name]: value } })),
  setVariables: (variables) => set({ variables }),
  setIsLoadingLibraries: (isLoadingLibraries) => set({ isLoadingLibraries }),
  setIsLoadingPrompts: (isLoadingPrompts) => set({ isLoadingPrompts }),
  setIsGenerating: (isGenerating) =>
    set(isGenerating ? { isGenerating } : { isGenerating, generationProgress: [] }),
  setResult: (result) => set({ result }),
  setGenerationProgress: (generationProgress) => set({ generationProgress }),
  setWriteError: (writeError) => set({ writeError }),
  clearResult: () => set({ result: null, writeError: null, generationProgress: [] }),

  reset: () =>
    set({
      libraries: [],
      selectedLibraryId: null,
      prompts: [],
      selectedPromptId: null,
      variables: {},
      isLoadingLibraries: false,
      isLoadingPrompts: false,
      isGenerating: false,
      result: null,
      generationProgress: [],
      writeError: null,
    }),
}));
